import { PersonalContentPermissionService } from '@aipb/gulliver/personal-content/auth';
import { FavoriteLibService } from '@aipb/gulliver/personal-content/data-access';
import { CopyNibbleDialogService, CreateEditNibbleDialogService } from '@aipb/gulliver/personal-content/dialog';
import { Collection, NibbleCut, NibbleMenuContext, NibbleType } from '@aipb/gulliver/personal-content/model';
import { PersonalContentRouting } from '@aipb/gulliver/personal-content/routing';
import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, inject, Input, Output } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatMenuModule } from '@angular/material/menu';
import { TranslocoModule } from '@jsverse/transloco';
import {
  AddAfterUseCase,
  AddBeforeUseCase,
  CopyUrlUseCase,
  DetailUseCase,
  DownloadUseCase,
  MoveToUseCase,
  RemoveUseCase
} from '../usecases';
import { MatIconModule } from '@angular/material/icon';

@Component({
  selector: 'pc-shared-nibble-menu',
  templateUrl: './nibble-menu.component.html',
  styleUrls: ['./nibble-menu.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [CommonModule, MatButtonModule, MatMenuModule, MatIconModule, MatDividerModule, TranslocoModule]
})
export class NibbleMenuComponent {
  @Input() nibbleCut: NibbleCut;
  @Input() collection: Collection;
  @Input() position: number;
  @Input() hasButtonBackground = false;
  @Input() context: NibbleMenuContext;

  @Output() modifyCrop = new EventEmitter<NibbleCut>();
  @Output() editNibble = new EventEmitter<NibbleCut>();
  @Output() updateNibble = new EventEmitter<NibbleCut>();

  private detail = inject(DetailUseCase);
  private download = inject(DownloadUseCase);
  private moveTo = inject(MoveToUseCase);
  private remove = inject(RemoveUseCase);
  private copyUrl = inject(CopyUrlUseCase);
  private addAfter = inject(AddAfterUseCase);
  private addBefore = inject(AddBeforeUseCase);

  constructor(
    private favoriteLibService: FavoriteLibService,
    private personalContentRouting: PersonalContentRouting,
    private personalContentPermission: PersonalContentPermissionService,
    private copyNibbleDialogService: CopyNibbleDialogService,
    private createEditNibbleDialogService: CreateEditNibbleDialogService
  ) {}

  get isFavorites() {
    return this.collection.type === 'favorites';
  }

  get hideCollections(): boolean {
    return !this.personalContentPermission.canUseCollections;
  }

  get hasDetailButton() {
    const type = this.nibbleCut.nibble.type;
    const supported: NibbleType[] = ['IMG', 'IMG3F', 'CROP', 'TXT', 'DOC', 'DOC3IF', 'REC', 'LINK', 'VID', 'COLL'];

    return supported.includes(type) && this.includesContexts(['plugin', 'summary', 'crop-list']);
  }

  get hasCopyNibbleUrlButton() {
    const type = this.nibbleCut.nibble.type;
    const supported: NibbleType[] = ['IMG', 'IMG3F', 'CROP', 'DOC', 'DOC3IF', 'REC', 'LINK', 'VID', 'COLL'];

    return supported.includes(type) && this.includesContexts(['plugin', 'summary', 'crop-list']);
  }

  get hasDownloadButton() {
    const type = this.nibbleCut.nibble.type;
    const supported: NibbleType[] = ['IMG3F', 'CROP'];

    return supported.includes(type) && this.includesContexts(['plugin', 'summary', 'crop-list']);
  }

  get hasModifyButton() {
    const type = this.nibbleCut.nibble.type;
    const supported: NibbleType[] = ['CROP'];

    return supported.includes(type) && this.includesContexts(['crop-list']);
  }

  get hasEditNibbleButton() {
    const type = this.nibbleCut.nibble.type;
    const supported: NibbleType[] = ['IMG', 'IMG3F', 'CROP', 'TXT', 'DOC', 'DOC3IF', 'REC', 'LINK', 'VID'];

    return supported.includes(type) && this.includesContexts(['plugin', 'summary', 'crop-list']);
  }

  get hasEditCollectionButton() {
    const type = this.nibbleCut.nibble.type;
    const supported: NibbleType[] = ['COLL'];

    return supported.includes(type) && this.includesContexts(['plugin', 'summary']);
  }

  get hasCopyToButton() {
    const type = this.nibbleCut.nibble.type;
    const supported: NibbleType[] = ['IMG', 'IMG3F', 'CROP', 'TXT', 'DOC', 'DOC3IF', 'REC', 'LINK', 'VID', 'COLL'];

    return supported.includes(type) && this.includesContexts(['plugin', 'summary']);
  }

  get hasMoveToButton() {
    const type = this.nibbleCut.nibble.type;
    const supported: NibbleType[] = ['IMG', 'IMG3F', 'CROP', 'TXT', 'DOC', 'DOC3IF', 'REC', 'LINK', 'VID'];

    return this.collection?.id && supported.includes(type) && this.includesContexts(['plugin', 'summary']);
  }

  get hasAddAfterButton() {
    const type = this.nibbleCut.nibble.type;
    const supported: NibbleType[] = ['IMG', 'IMG3F', 'CROP', 'TXT', 'DOC', 'DOC3IF', 'REC', 'LINK', 'VID', 'COLL'];

    return this.collection?.id && this.position >= 0 && supported.includes(type) && this.includesContexts(['summary']);
  }

  get hasAddBeforeButton() {
    const type = this.nibbleCut.nibble.type;
    const supported: NibbleType[] = ['IMG', 'IMG3F', 'CROP', 'TXT', 'DOC', 'DOC3IF', 'REC', 'LINK', 'VID', 'COLL'];

    return this.collection?.id && this.position >= 0 && supported.includes(type) && this.includesContexts(['summary']);
  }

  get hasRemoveButton() {
    const type = this.nibbleCut.nibble.type;
    const supported: NibbleType[] = ['IMG', 'IMG3F', 'CROP', 'TXT', 'DOC', 'DOC3IF', 'REC', 'LINK', 'VID', 'COLL'];

    return this.collection?.id && supported.includes(type) && this.includesContexts(['plugin', 'summary']);
  }

  onDetail() {
    this.detail.execute(this.nibbleCut);
  }

  onEditNibble() {
    this.createEditNibbleDialogService
      .open$({
        editNibble: this.nibbleCut
      })
      .subscribe(() => {
        this.favoriteLibService.refreshPlugin();
        this.editNibble.emit(this.nibbleCut);
      });
  }

  onModifyCrop() {
    this.modifyCrop.emit(this.nibbleCut);
  }

  onDownload() {
    this.download.execute(this.nibbleCut);
  }

  onEditCollection() {
    this.personalContentRouting.collectionSingle(this.nibbleCut.cut.serviceValue);
  }

  onCopyTo() {
    this.copyNibbleDialogService
      .open$({
        nibbleCut: this.nibbleCut,
        collectionId: this.collection.id
      })
      .subscribe(() => {
        this.updateNibble.emit(this.nibbleCut);
        this.favoriteLibService.refreshPlugin();
      });
  }

  onMoveTo() {
    this.moveTo
      .execute({
        nibbleCut: this.nibbleCut,
        collectionId: this.collection.id
      })
      .subscribe(() => {
        this.updateNibble.emit(this.nibbleCut);
      });
  }

  onRemove() {
    this.remove.execute({
      nibbleCut: this.nibbleCut,
      collectionId: this.collection.id,
      isFavorites: this.isFavorites
    });
  }

  onCopyNibbleUrl() {
    this.copyUrl.execute(this.nibbleCut);
  }

  onAddBefore() {
    this.addBefore.execute({
      collectionId: this.collection.id,
      position: this.position
    });
  }

  onAddAfter() {
    this.addAfter.execute({
      collectionId: this.collection.id,
      position: this.position
    });
  }

  private includesContexts(contexts: NibbleMenuContext[] = []) {
    return contexts.includes(this.context);
  }
}
